import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { lt, showSuccessAlert } from '@mews/framework';
import { useRepositoryMutation, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { CentralPaymentPolicyFormContent } from '../CentralPaymentPolicyFormContent';
import { preparePaymentPolicyValues } from '../../../utils';
import { FormValues, resolver } from '../schema';
import { useGetInitialFormValues } from './useGetInitialFormValues';

interface Params {
    paymentPolicyId: string;
}

export const EditCentralPaymentPolicyForm = ({ paymentPolicyId, ...other }: CommanderFormProps & Params) => {
    const { data } = useRepositorySuspenseQuery('portfolioCentralPaymentPolicies', 'get', {
        centralPaymentPolicyId: paymentPolicyId,
    });

    const { mutateAsync } = useRepositoryMutation('portfolioCentralPaymentPolicies', 'update', {
        invalidateQueries: {
            portfolioCentralPaymentPolicies: ['get', 'getOverviewEntries', 'getAll'],
            portfolioCentralRateGroups: ['getAll', 'getOverview'],
        },
        onSuccess: () => showSuccessAlert({ title: lt('PaymentPolicyUpdatedSuccessMessage') }),
        transformParams: (values: FormValues) => ({
            ...preparePaymentPolicyValues(values),
            centralPaymentPolicyId: paymentPolicyId,
        }),
    });

    const { formValues } = useGetInitialFormValues(data.centralPaymentPolicy);

    const { renderFormContent } = useHandledDialogFormControls({
        id: `update-cancellation-policy-${paymentPolicyId}`,
        onSubmit: async (values) => mutateAsync(values),
        defaultValues: formValues,
        resolver,
        ...other,
    });

    return renderFormContent(<CentralPaymentPolicyFormContent disableAmountType />);
};

export const useEditCentralPaymentPolicyDialogForm = ({ paymentPolicyId }: Params) =>
    useDialogForm({
        title: lt('PaymentPolicyEdit'),
        renderForm: (props) => <EditCentralPaymentPolicyForm paymentPolicyId={paymentPolicyId} {...props} />,
    });
